import { useState, type FormEvent, type KeyboardEvent } from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";

import { Button } from "@/components/ui/button";
import { CHANNEL_LIMITS } from "@/lib/channel-limits";
import { cn } from "@/lib/utils";
import type { Channel } from "@/types";

interface MessageInputProps {
  channel: Channel;
  hasPendingFailure: boolean;
  onSend: (content: string) => void;
  mobileMode?: boolean;
}

export function MessageInput({
  channel,
  hasPendingFailure,
  onSend,
  mobileMode = false,
}: MessageInputProps) {
  const [draft, setDraft] = useState("");
  const [shakeKey, setShakeKey] = useState(0);

  const limit = CHANNEL_LIMITS[channel];
  const len = draft.length;
  const ratio = limit > 0 ? len / limit : 0;
  const showCounter = ratio > 0.7;
  const overLimit = len > limit;
  const trimmed = draft.trim();
  const canSend = trimmed.length > 0 && !overLimit && !hasPendingFailure;

  const counterColor =
    ratio >= 1
      ? "text-destructive"
      : ratio >= 0.9
        ? "text-warning"
        : "text-muted-foreground";

  function handleSubmit() {
    if (overLimit) {
      setShakeKey((k) => k + 1);
      return;
    }
    if (!canSend) return;
    onSend(trimmed);
    setDraft("");
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  function handleFormSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    handleSubmit();
  }

  return (
    <form onSubmit={handleFormSubmit} className="w-full">
      <div className={cn("mx-auto", mobileMode ? "max-w-full" : "max-w-2xl")}>
        <motion.div
          key={shakeKey}
          animate={shakeKey > 0 ? { x: [-4, 4, -4, 4, 0] } : { x: 0 }}
          transition={{ duration: 0.2 }}
          className={cn("flex items-end", mobileMode ? "gap-2" : "gap-3")}
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={hasPendingFailure}
            placeholder={
              mobileMode
                ? "Escribe un mensaje..."
                : "Escribe un mensaje y presiona Enter para enviar"
            }
            className={cn(
              "flex-1 resize-none rounded-lg border bg-surface-2 px-3.5 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 disabled:opacity-60",
              overLimit
                ? "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30"
                : "border-border focus-visible:border-brand focus-visible:ring-brand/30",
              mobileMode ? "min-h-11 py-2.5 text-sm" : "py-2.5 text-sm"
            )}
          />
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              type="submit"
              disabled={!canSend}
              className={mobileMode ? "min-h-11" : undefined}
            >
              <Send className="size-4" />
              <span className={cn(mobileMode && "sr-only")}>Enviar</span>
            </Button>
          </motion.div>
        </motion.div>

        {(showCounter || !mobileMode) && (
          <div
            className={cn(
              "mt-2 flex items-center text-[11px]",
              showCounter ? "justify-between" : "justify-end"
            )}
          >
            {showCounter && (
              <span className={cn("tabular-nums", counterColor)}>
                {len} / {limit}
              </span>
            )}
            {!mobileMode && (
              <span className="text-muted-foreground">
                ⏎ enviar · ⇧⏎ nueva línea
              </span>
            )}
          </div>
        )}
      </div>
    </form>
  );
}
